import {
	EventDto,
	EventMediaProviderMediaType,
	MarketDto,
	MediaProviderDto,
	MediaProviderName,
	SelectionDto
} from './api/sbapi/sbapi-api-model';

export type MarketWithSelections = MarketDto & {
	selections: Array<SelectionDto>;
	minOdds?: number;
	maxOdds?: number;
};

export type EventWithMarketsAndSelections = EventDto & {
	markets: Array<MarketWithSelections>;
};

export type EventWithMergedMarketsAndSelections = EventWithMarketsAndSelections & {
	markets: Array<MarketWithMergedMarketsAndSelections>;
};

export type MarketWithMergedMarketsAndSelections = MarketWithSelections & {
	mergedMarkets?: MarketWithSelections[];
};

export type EventsGroupedByStartDate = {
	startDate: string;
	events: Array<EventWithMarketsAndSelections>;
};

export type EventsWithLayoutMarketsAndSelections = EventDto & {
	markets: Array<MarketWithSelections>;
};

export type SportsbookTranslations<T extends string> = Record<T, string>;

export enum BetType {
	SINGLE = 'Single',
	COMBO = 'Combo',
	SYSTEM = 'System'
}

export enum BetStatus {
	SETTLED = 'SETTLED',
	UNSETTLED = 'UNSETTLED'
}

export enum BetSettlementStatus {
	OPEN = 'OPEN',
	WON = 'WON',
	LOST = 'LOST',
	CANCELLED = 'CANCELLED',
	CASHOUT = 'CASHOUT'
}

export enum Currency {
	EUR = 'EUR',
	PLN = 'PLN',
	SEK = 'SEK',
	DKK = 'DKK',
	NOK = 'NOK',
	CAD = 'CAD'
}

export enum LeagueWidgetTypes {
	LIVE_TABLE = 'LiveTable'
}
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const SportradarWidgetTypes = { ...EventMediaProviderMediaType, ...LeagueWidgetTypes };
export type SportradarWidgetType = typeof SportradarWidgetTypes;

export type SportradarEventWidgetType =
	| EventMediaProviderMediaType.MATCH_PREVIEW
	| EventMediaProviderMediaType.MATCH_TRACKER;

export function isSportradarWidgetProvider(
	mediaProvider: MediaProviderDto
): mediaProvider is MediaProviderDto<SportradarEventWidgetType> {
	const hasCorrectName = mediaProvider.providerName === MediaProviderName.SPORTRADAR;
	const hasCorrectType =
		mediaProvider.mediaType === EventMediaProviderMediaType.MATCH_PREVIEW ||
		mediaProvider.mediaType === EventMediaProviderMediaType.MATCH_TRACKER;

	return hasCorrectName && hasCorrectType;
}

export function isBetGeniusStreamingWidgetProvider(
	mediaProvider: MediaProviderDto
): mediaProvider is MediaProviderDto<EventMediaProviderMediaType.LIVESTREAMING> {
	const hasCorrectName = mediaProvider.providerName === MediaProviderName.BETGENIUS;
	const hasCorrectType = mediaProvider.mediaType === EventMediaProviderMediaType.LIVESTREAMING;

	return hasCorrectName && hasCorrectType;
}

export enum ParticipantVenueRole {
	HOME = 'Home',
	AWAY = 'Away'
}

export enum SelectionOutcomeType {
	HOME = 'Home',
	AWAY = 'Away',
	TIE = 'Tie',
	// OU
	UNDER = 'Under',
	OVER = 'Over',
	// Yes No
	YES = 'Yes',
	NO = 'No',
	// Goalscorer
	TO_SCORE_LAST = 'ToScoreLast',
	TO_SCORE_ANYTIME = 'ToScoreAnyTime',
	TO_SCORE_FIRST = 'ToScoreFirst',
	TO_SCORE_20_MORE = 'ToScore2OrMore',
	TO_SCORE_FIRST_OR_LAST = 'ToScoreFirstOrLast',
	TO_SCORE_30_MORE = 'ToScore3OrMore'
}

export type SelectionGroup = Array<SelectionDto>;

export const MARKET_GROUP_OTHER_ID = '0';

export enum MarketTypeName {
	NAME_1X2 = '1X2',
	NAME_WINNER = 'Winner'
}

/**
 * Make properties K in T optional
 */
export type PartialBy<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;

export enum BetslipVisibility {
	HIDDEN = 'HIDDEN',
	MINIMIZED = 'MINIMIZED',
	EXPANDED = 'EXPANDED'
}

export enum OddsFormat {
	DECIMAL = 'DECIMAL',
	AMERICAN = 'AMERICAN'
}

export enum LocationVerificationStatus {
	PENDING = 'PENDING',
	ALLOWED = 'ALLOWED',
	DENIED = 'DENIED',
	ERROR = 'ERROR'
}
